import { useState } from 'react';

import { RotateCcw } from 'lucide-react';

import { Button, cn, Input, StatusChip } from '@src/components/ui';
import {
  useOptionLabels,
  useReturnWindowSettingsMutation,
  useReturnWindowSettingsQuery,
} from '@src/query';

import { buildReturnWindowPayload, isValidDays, returnWindowDefaults } from './returnWindowForm';


/** One channel's days input + a "Default" hint chip when the org has no override
 * for it (the resolved value is the per-channel default: eBay 30d / others 14d).
 * An out-of-range / blank edit shows an inline "Enter 1–365 days" error (icon-free
 * text is fine at this size — the red border marks the field too). */
function ReturnWindowRow({ row, label, value, invalid, canWrite, busy, onChange }) {
  return (
    <div className="flex items-center gap-3.5 rounded-lg border border-hairline bg-surface-1 px-4 py-3">
      <span className="grid size-[38px] shrink-0 place-items-center rounded-lg bg-surface-2 text-ink-subtle">
        <RotateCcw className="size-[18px]" />
      </span>

      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="flex items-center gap-2">
          <span className="truncate text-sm font-semibold text-ink">{label}</span>

          {row.is_default
            ? (
              <StatusChip variant="neutral" className="px-2 py-[2px] text-[10.5px]" bordered={false}>
                Default
              </StatusChip>
            )
            : null}
        </span>

        <span className="text-xs text-ink-subtle">Advisory return window — never scraps a part for you.</span>
      </span>

      <span className="flex shrink-0 flex-col items-end gap-1">
        <span className="flex items-center gap-2">
          <Input
            min="1"
            max="365"
            type="number"
            value={value ?? ''}
            readOnly={!canWrite}
            disabled={!canWrite || busy}
            aria-invalid={invalid || undefined}
            aria-label={`${label} return window in days`}
            className={cn('h-9 w-20 text-center', invalid && 'border-danger focus-visible:ring-danger/40')}
            inputMode="numeric"
            onChange={(event) => onChange(row.channel, event.target.value)}
          />

          <span className="text-xs text-ink-subtle">days</span>
        </span>

        {invalid
          ? <span className="text-xs font-medium text-danger">Enter 1–365 days</span>
          : null}
      </span>
    </div>
  );
}

ReturnWindowRow.displayName = 'ReturnWindowRow';


/**
 * Settings → Channels return-window days editor (M25 #437). One row per
 * SaleChannel with its resolved days + a "Default" hint chip; edits persist via a
 * PUT that upserts only the CHANGED rows (`buildReturnWindowPayload`). Owner/Manager
 * write; the enclosing Channels page is already Owner/Manager-gated, so `canWrite`
 * is defensive read-only (disabled inputs + hidden Save) should that ever loosen.
 */
function ReturnWindowSettings({ sellerId, canWrite, onToast }) {
  const settingsQuery = useReturnWindowSettingsQuery(sellerId);
  const queryKey = settingsQuery.queryKey;
  const rows = settingsQuery.data;
  const labels = useOptionLabels('sale_channel');

  const mutation = useReturnWindowSettingsMutation();

  // Sync the controlled edit map whenever the server rows load / refetch, using
  // the documented "adjust state during render" pattern (React resets state on a
  // changed dependency without an effect — the repo bans setState in effects).
  // The query data ref is stable across renders unless it actually changes, so
  // edits reset only on a real data change (initial load / post-save refetch).
  const [edited, setEdited] = useState({});
  const [syncedRows, setSyncedRows] = useState(null);
  if (rows && rows !== syncedRows) {
    setSyncedRows(rows);
    setEdited(returnWindowDefaults(rows));
  }

  const onChange = (channel, next) => {
    setEdited((current) => ({ ...current, [channel]: next }));
  };

  // Live per-row validation: any blank / out-of-range (1–365) edit blocks Save
  // with a visible inline error instead of being silently dropped from the PUT.
  const invalidChannels = new Set(
    Object.keys(edited).filter((channel) => !isValidDays(edited[channel])),
  );
  const hasInvalid = invalidChannels.size > 0;

  const save = () => {
    if (hasInvalid) {
      onToast('Fix the highlighted return windows first (1–365 days)', 'danger');
      return;
    }
    const payload = buildReturnWindowPayload(edited, rows);
    if (payload.length === 0) {
      onToast('No return-window changes to save');
      return;
    }
    mutation.mutate(
      { sellerId, rows: payload, queryKey },
      {
        onSuccess: () => onToast('Return windows updated'),
        onError: () => onToast('Could not save return windows', 'danger'),
      },
    );
  };

  return (
    <section className="flex flex-col gap-2.5">
      <div className="flex items-center justify-between gap-3 px-4 lg:px-0">
        <span className="flex flex-col gap-0.5">
          <h2 className="text-sm font-semibold text-ink">Return windows</h2>

          <span className="text-xs text-ink-subtle">
            How long after a sale a part might bounce back — so you don&apos;t scrap it too soon.
          </span>
        </span>

        {canWrite
          ? (
            <Button
              type="button"
              className="h-[38px] shrink-0 text-table"
              onClick={save}
              loading={mutation.isPending}
            >
              Save
            </Button>
          )
          : null}
      </div>

      {settingsQuery.isPending
        ? <span className="py-6 text-center text-table text-ink-subtle">Loading return windows…</span>
        : settingsQuery.isError
          ? <span className="py-6 text-center text-table text-danger">Could not load return windows.</span>
          : (rows || []).map((row) => (
            <ReturnWindowRow
              key={row.channel}
              canWrite={canWrite}
              busy={mutation.isPending}
              value={edited[row.channel]}
              invalid={invalidChannels.has(row.channel)}
              label={labels[row.channel] || row.channel}
              row={row}
              onChange={onChange}
            />
          ))}

      <span className="px-4 text-xs text-ink-subtle lg:px-0">
        Advisory only — AutoOrbit never changes a part or price when a window closes.
      </span>
    </section>
  );
}

ReturnWindowSettings.displayName = 'ReturnWindowSettings';
export default ReturnWindowSettings;
